import { useEffect, useMemo, useState } from "react";
import { adminGet } from "./adminApi";
import "./Admin.css";

function safeStr(v) {
  if (v == null) return "";
  return String(v);
}

function fmtDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString();
}

function fmtDay(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString();
}

function getBookingObj(b) {
  return b?.booking && typeof b.booking === "object" ? b.booking : b;
}

function pickGuest(b) {
  const booking = getBookingObj(b);

  const email =
    b?.guestEmail ||
    booking?.guestEmail ||
    booking?.email ||
    booking?.userEmail ||
    booking?.user?.email ||
    booking?.guest?.email ||
    "";

  const name =
    b?.guestName ||
    booking?.guestName ||
    booking?.name ||
    booking?.user?.name ||
    booking?.guest?.name ||
    "";

  const phone =
    b?.guestPhone ||
    booking?.guestPhone ||
    booking?.phone ||
    booking?.guest?.phone ||
    booking?.user?.phone ||
    "";

  return {
    email: safeStr(email),
    name: safeStr(name),
    phone: safeStr(phone),
  };
}

function pickDates(b) {
  const booking = getBookingObj(b);

  const checkIn =
    b?.checkIn ||
    booking?.checkIn ||
    booking?.startDate ||
    booking?.from ||
    (Array.isArray(booking?.dates) ? booking.dates[0] : null) ||
    null;

  const checkOut =
    b?.checkOut ||
    booking?.checkOut ||
    booking?.endDate ||
    booking?.to ||
    (Array.isArray(booking?.dates) ? booking.dates[booking.dates.length - 1] : null) ||
    null;

  return { checkIn, checkOut };
}

function pickPropertyId(b) {
  return safeStr(
    b?.propertyId ||
      b?.property ||
      b?.property?._id ||
      b?.property?.id ||
      ""
  );
}

function pickPropertyTitle(b) {
  return safeStr(
    b?.propertyTitle ||
      b?.property?.title ||
      b?.title ||
      ""
  );
}

function pickPropertyLocation(b) {
  return safeStr(
    b?.propertyLocation ||
      b?.property?.location ||
      b?.location ||
      ""
  );
}

function pickHostEmail(b) {
  return safeStr(
    b?.hostEmail ||
      b?.ownerEmail ||
      b?.propertyOwnerEmail ||
      b?.host?.email ||
      ""
  );
}

function pickBookingId(b) {
  const booking = getBookingObj(b);
  return safeStr(b?._id || b?.bookingId || booking?._id || booking?.id || "");
}

function pickCreatedAt(b) {
  const booking = getBookingObj(b);
  return b?.createdAt || booking?.createdAt || "";
}

function pickCancelledAt(b) {
  const booking = getBookingObj(b);
  return b?.cancelledAt || booking?.cancelledAt || "";
}

function pickStatus(b) {
  const booking = getBookingObj(b);
  const raw = safeStr(b?.status || booking?.status || "").trim().toLowerCase();

  if (raw === "cancelled_by_host") return "Cancelled by host";
  if (raw === "cancelled_by_admin") return "Cancelled by admin";
  if (raw === "active") return "Active";

  return raw ? raw : "Active";
}

function isCancelledStatus(b) {
  const booking = getBookingObj(b);
  const raw = safeStr(b?.status || booking?.status || "").trim().toLowerCase();
  return raw === "cancelled_by_host" || raw === "cancelled_by_admin";
}

function pickTotal(b) {
  const booking = getBookingObj(b);

  const t =
    b?.total ??
    booking?.total ??
    b?.totalPrice ??
    booking?.totalPrice ??
    b?.price ??
    booking?.price ??
    b?.amount ??
    booking?.amount ??
    b?.paidAmount ??
    booking?.paidAmount;

  if (t == null || t === "") return "—";

  const n = Number(t);
  if (!Number.isFinite(n)) return "—";

  return `€${n.toFixed(0)}`;
}

function normalizeResponseToArray(data) {
  if (Array.isArray(data)) return data;

  if (data && typeof data === "object") {
    if (Array.isArray(data.bookings)) return data.bookings;
    if (Array.isArray(data.items)) return data.items;
    if (Array.isArray(data.data)) return data.data;
    if (Array.isArray(data.results)) return data.results;
    if (data.success && Array.isArray(data.bookings)) return data.bookings;
  }

  return [];
}

function pickErrorMessage(e) {
  const raw = safeStr(e?.message || e || "");
  if (!raw) return "Unknown error";

  if (raw.includes("API route not found") || raw.includes("404")) {
    return "❌ /api/admin/bookings не съществува в backend-а (404). Трябва да добавим admin bookings endpoint в server.js.";
  }
  if (raw.toLowerCase().includes("not authorized") || raw.includes("401")) {
    return "❌ 401 Not authorized. Не си логнат като admin или cookie/token не се праща.";
  }
  if (raw.includes("Admin only") || raw.includes("403")) {
    return "❌ 403 Admin only. Потребителят ти не е с role=admin в Mongo.";
  }

  return raw;
}

export default function AdminBookings() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [debugInfo, setDebugInfo] = useState("");

  async function load() {
    setLoading(true);
    setErr("");
    setDebugInfo("");

    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (from) params.set("from", from);
      if (to) params.set("to", to);

      const path = `/api/admin/bookings${params.toString() ? `?${params}` : ""}`;
      const data = await adminGet(path);

      const shape = Array.isArray(data)
        ? `array(${data.length})`
        : data && typeof data === "object"
        ? `object keys: ${Object.keys(data).slice(0, 8).join(", ")}`
        : typeof data;

      setDebugInfo(`API response: ${shape}`);

      const arr = normalizeResponseToArray(data);
      setItems(arr);
    } catch (e) {
      setItems([]);
      setErr(pickErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = useMemo(() => {
    const query = q.trim().toLowerCase();
    let list = items || [];

    if (query) {
      list = list.filter((b) => {
        const bid = pickBookingId(b);
        const pid = pickPropertyId(b);
        const propertyTitle = pickPropertyTitle(b);
        const propertyLocation = pickPropertyLocation(b);
        const hostEmail = pickHostEmail(b);
        const guest = pickGuest(b);
        const status = pickStatus(b);

        const blob =
          `${bid} ${pid} ${propertyTitle} ${propertyLocation} ${hostEmail} ${guest.email} ${guest.name} ${guest.phone} ${status}`.toLowerCase();

        return blob.includes(query);
      });
    }

    if (from) {
      list = list.filter((b) => {
        const { checkIn } = pickDates(b);
        return !checkIn || String(checkIn) >= from;
      });
    }

    if (to) {
      list = list.filter((b) => {
        const { checkOut } = pickDates(b);
        return !checkOut || String(checkOut) <= to;
      });
    }

    return list;
  }, [items, q, from, to]);

  return (
    <div className="admin-page">
      <div className="admin-page__top">
        <div>
          <h1 className="admin-page__title">Admin – Bookings</h1>
          <p className="admin-page__subtitle">
            Review reservations, guests, properties, host emails, statuses and booking totals.
          </p>
        </div>

        <button className="admin-btn admin-btn--ghost" type="button" onClick={load}>
          {loading ? "Loading..." : "Refresh"}
        </button>
      </div>

      <div className="admin-toolbar">
        <input
          className="admin-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search: guest / hotel / host email / booking id / property id / status"
        />

        <div className="admin-dateFilters">
          <div className="admin-dateGroup">
            <label className="admin-dateLabel">From</label>
            <input
              className="admin-dateInput"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>

          <div className="admin-dateGroup">
            <label className="admin-dateLabel">To</label>
            <input
              className="admin-dateInput"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
        </div>
      </div>

      {!!debugInfo && <div className="admin-debug">{debugInfo}</div>}

      {err && <div className="admin-alert">{err}</div>}

      <div className="admin-statsRow">
        <div className="admin-statCard">
          <span className="admin-statCard__label">Total loaded</span>
          <strong className="admin-statCard__value">{items.length}</strong>
        </div>

        <div className="admin-statCard">
          <span className="admin-statCard__label">Visible after filters</span>
          <strong className="admin-statCard__value">{rows.length}</strong>
        </div>

        <div className="admin-statCard">
          <span className="admin-statCard__label">Status</span>
          <strong className="admin-statCard__value">{loading ? "Loading..." : "Ready"}</strong>
        </div>
      </div>

      <div className="admin-tableWrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th align="left">Booking ID</th>
              <th align="left">Hotel / Property</th>
              <th align="left">Guest</th>
              <th align="left">Host Email</th>
              <th align="left">Check-in</th>
              <th align="left">Check-out</th>
              <th align="left">Paid</th>
              <th align="left">Status</th>
              <th align="left">Created</th>
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 && !loading ? (
              <tr>
                <td colSpan="9" className="admin-emptyCell">
                  No bookings found.
                </td>
              </tr>
            ) : (
              rows.map((b) => {
                const bid = pickBookingId(b);
                const pid = pickPropertyId(b);
                const propertyTitle = pickPropertyTitle(b);
                const propertyLocation = pickPropertyLocation(b);
                const hostEmail = pickHostEmail(b);
                const guest = pickGuest(b);
                const { checkIn, checkOut } = pickDates(b);
                const createdAt = pickCreatedAt(b);
                const cancelledAt = pickCancelledAt(b);
                const statusLabel = pickStatus(b);
                const isCancelled = isCancelledStatus(b);
                const key = `${pid || "p"}-${bid || "b"}`;

                return (
                  <tr key={key}>
                    <td>
                      <span className="admin-mono">{bid || "—"}</span>
                    </td>

                    <td>
                      <div className="admin-propertyCell">
                        <strong>{propertyTitle || "—"}</strong>
                        <span className="admin-propertyId">{propertyLocation || pid || "—"}</span>
                      </div>
                    </td>

                    <td>
                      <div className="admin-propertyCell">
                        <strong>{guest.name || "—"}</strong>
                        <span className="admin-propertyId">{guest.email || "—"}</span>
                        <span className="admin-propertyId">{guest.phone || "—"}</span>
                      </div>
                    </td>

                    <td>{hostEmail || "—"}</td>

                    <td>{fmtDay(checkIn)}</td>
                    <td>{fmtDay(checkOut)}</td>

                    <td>
                      <span className="admin-badge admin-badge--visible">{pickTotal(b)}</span>
                    </td>

                    <td>
                      <div className="admin-propertyCell">
                        <strong>{statusLabel}</strong>
                        {isCancelled && (
                          <span className="admin-propertyId">
                            {cancelledAt ? `Cancelled: ${fmtDate(cancelledAt)}` : "Cancelled"}
                          </span>
                        )}
                      </div>
                    </td>

                    <td>{fmtDate(createdAt)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}